import { useState, useEffect, useCallback, useRef } from "react";
import { API } from "@/lib/constants";
import { toast } from "sonner";
import { Palette, Building2, Layers, Plus, Trash2, Loader2, Pencil, Star, Save, X, Image as ImageIcon, Upload, RotateCcw } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

/**
 * Cover library admin — Settings → Cover library. Admin-only.
 *
 * Three CRUD cards for the orthogonal cover-prompt dimensions:
 *   1. Sector prompts        — list w/ evidence-backed badge + manual flip
 *   2. Org-type modifiers     — list w/ one-default management
 *   3. Colour presets         — list w/ one-default management
 *
 * Editing a sector prompt's TEXT auto-invalidates its evidence-backed
 * flag server-side (the operator must re-confirm a real winning cover
 * before re-locking) — same discipline as render-hash invalidation.
 */
export default function CoverLibraryAdmin() {
  return (
    <div className="space-y-6" data-testid="cover-library-admin">
      <div>
        <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <Layers className="w-5 h-5 text-indigo-700" />
          Cover library
        </h2>
        <p className="text-sm text-slate-600 mt-1">
          Three orthogonal dimensions assemble at cover generation: sector (what
          to render) · organisation type (how it should feel) · colour (palette).
          None overlap — keep colour and tonal language out of sector prompts.
        </p>
      </div>
      <SectorPromptsCard />
      <OrgTypesCard />
      <ColourPresetsCard />
      <BrandAssetsCard />
    </div>
  );
}

// ─── Sector prompts ───────────────────────────────────────────────────
function SectorPromptsCard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editKey, setEditKey] = useState(null);
  const [draft, setDraft] = useState({ sector_key: "", label: "", prompt_template: "", evidence_backed: false });
  const [saving, setSaving] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/v2/admin/cover-sector-prompts`, { credentials: "include" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setRows(j.sectors || []);
    } catch (e) { toast.error(`Load failed: ${e.message}`); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchRows(); }, [fetchRows]);

  const startEdit = (row) => {
    setEditKey(row.sector_key);
    setDraft({ sector_key: row.sector_key, label: row.label, prompt_template: row.prompt_template, evidence_backed: !!row.evidence_backed });
  };
  const startNew = () => {
    setEditKey("__new__");
    setDraft({ sector_key: "", label: "", prompt_template: "", evidence_backed: false });
  };
  const cancel = () => { setEditKey(null); };

  const save = async () => {
    const key = (editKey === "__new__" ? draft.sector_key : editKey).trim().toLowerCase();
    if (!key) { toast.error("Sector key required"); return; }
    if (draft.label.trim().length < 2) { toast.error("Label required"); return; }
    if (draft.prompt_template.trim().length < 10) { toast.error("Prompt too short"); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API}/v2/admin/cover-sector-prompts/${encodeURIComponent(key)}`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ label: draft.label.trim(), prompt_template: draft.prompt_template.trim(), evidence_backed: draft.evidence_backed }),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e?.detail || `HTTP ${r.status}`); }
      toast.success("Sector saved");
      setEditKey(null);
      fetchRows();
    } catch (e) { toast.error(`Save failed: ${e.message}`); }
    finally { setSaving(false); }
  };

  const remove = async (key) => {
    if (!window.confirm(`Delete sector "${key}"?`)) return;
    try {
      const r = await fetch(`${API}/v2/admin/cover-sector-prompts/${encodeURIComponent(key)}`, { method: "DELETE", credentials: "include" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success(`Deleted "${key}"`);
      fetchRows();
    } catch (e) { toast.error(`Delete failed: ${e.message}`); }
  };

  const setEvidence = async (key, value) => {
    const verb = value ? "Promote" : "Release";
    const msg = value
      ? `Promote "${key}" to evidence-backed?\n\nOnly confirm if THIS EXACT prompt wording produced a real winning cover. Editing the text later auto-clears the flag.`
      : `Release the evidence-backed lock on "${key}"?`;
    if (!window.confirm(msg)) return;
    try {
      const r = await fetch(`${API}/v2/admin/cover-sector-prompts/${encodeURIComponent(key)}/evidence-backed`, {
        method: "PATCH", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ evidence_backed: value }),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e?.detail || `HTTP ${r.status}`); }
      toast.success(value ? `"${key}" promoted to evidence-backed` : `"${key}" lock released`);
      fetchRows();
    } catch (e) { toast.error(`${verb} failed: ${e.message}`); }
  };

  return (
    <Card data-testid="cover-admin-sectors-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2">
          <Palette className="w-4 h-4 text-indigo-700" /> Sector prompts
        </CardTitle>
        <Button size="sm" variant="outline" onClick={startNew} data-testid="cover-admin-sector-add-btn">
          <Plus className="w-4 h-4 mr-1" /> New sector
        </Button>
      </CardHeader>
      <CardContent className="space-y-2">
        <p className="text-xs text-slate-500">
          ★ = evidence-backed (locked from a real winning cover). Editing a
          prompt's text auto-clears the flag — re-confirm a real cover before re-locking.
        </p>
        {editKey === "__new__" && (
          <SectorEditForm draft={draft} setDraft={setDraft} onSave={save} onCancel={cancel} saving={saving} isNew />
        )}
        {loading ? (
          <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : (
          <ul className="divide-y divide-slate-200 border border-slate-200 rounded">
            {rows.map((row) => (
              <li key={row.sector_key} className="px-3 py-2 text-sm" data-testid={`cover-admin-sector-${row.sector_key}`}>
                {editKey === row.sector_key ? (
                  <SectorEditForm draft={draft} setDraft={setDraft} onSave={save} onCancel={cancel} saving={saving} />
                ) : (
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-900 flex items-center gap-2">
                        {row.label}
                        {row.evidence_backed && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] bg-emerald-100 text-emerald-800 border border-emerald-300 rounded px-1.5 py-0.5" data-testid={`cover-admin-sector-evidence-${row.sector_key}`}>
                            <Star className="w-3 h-3" /> Evidence-backed
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-slate-500 font-mono">{row.sector_key}</div>
                      <div className="text-[11px] text-slate-600 mt-1 line-clamp-2">{row.prompt_template}</div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {row.evidence_backed ? (
                        <button onClick={() => setEvidence(row.sector_key, false)} className="text-emerald-700 hover:text-emerald-800 p-1" title="Release evidence-backed lock" data-testid={`cover-admin-sector-demote-${row.sector_key}`}><Star className="w-4 h-4 fill-current" /></button>
                      ) : (
                        <button onClick={() => setEvidence(row.sector_key, true)} className="text-slate-400 hover:text-emerald-700 p-1" title="Promote to evidence-backed" data-testid={`cover-admin-sector-promote-${row.sector_key}`}><Star className="w-4 h-4" /></button>
                      )}
                      <button onClick={() => startEdit(row)} className="text-slate-600 hover:text-slate-900 p-1" title="Edit" data-testid={`cover-admin-sector-edit-${row.sector_key}`}><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => remove(row.sector_key)} className="text-red-600 hover:text-red-700 p-1" title="Delete" data-testid={`cover-admin-sector-delete-${row.sector_key}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function SectorEditForm({ draft, setDraft, onSave, onCancel, saving, isNew }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded p-3 space-y-2" data-testid="cover-admin-sector-form">
      {isNew && (
        <div>
          <Label className="text-xs">Sector key (lowercase, underscores)</Label>
          <Input value={draft.sector_key} onChange={(e) => setDraft({ ...draft, sector_key: e.target.value })} placeholder="e.g. logistics" data-testid="cover-admin-sector-key-input" />
        </div>
      )}
      <div>
        <Label className="text-xs">Label</Label>
        <Input value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} data-testid="cover-admin-sector-label-input" />
      </div>
      <div>
        <Label className="text-xs">Prompt template (no colour or org-type language)</Label>
        <Textarea value={draft.prompt_template} onChange={(e) => setDraft({ ...draft, prompt_template: e.target.value })} rows={5} data-testid="cover-admin-sector-prompt-input" />
      </div>
      <div className="flex items-center gap-2">
        <Switch checked={draft.evidence_backed} onCheckedChange={(v) => setDraft({ ...draft, evidence_backed: v })} data-testid="cover-admin-sector-evidence-toggle" />
        <span className="text-xs text-slate-700">Evidence-backed (only after a real winning cover confirms this exact wording)</span>
      </div>
      <div className="flex gap-2">
        <Button size="sm" onClick={onSave} disabled={saving} className="bg-slate-800 hover:bg-slate-900 text-white" data-testid="cover-admin-sector-save-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}><X className="w-4 h-4 mr-1" /> Cancel</Button>
      </div>
    </div>
  );
}

// ─── Org-type modifiers ─────────────────────────────────────────────────
function OrgTypesCard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editKey, setEditKey] = useState(null);
  const [draft, setDraft] = useState({ org_type_key: "", display_label: "", modifier_clause: "", is_default: false });
  const [saving, setSaving] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/v2/admin/cover-org-types`, { credentials: "include" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setRows(j.org_types || []);
    } catch (e) { toast.error(`Load failed: ${e.message}`); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchRows(); }, [fetchRows]);

  const startEdit = (row) => { setEditKey(row.org_type_key); setDraft({ ...row }); };
  const startNew = () => { setEditKey("__new__"); setDraft({ org_type_key: "", display_label: "", modifier_clause: "", is_default: false }); };
  const cancel = () => setEditKey(null);

  const save = async () => {
    const key = (editKey === "__new__" ? draft.org_type_key : editKey).trim().toLowerCase();
    if (!key) { toast.error("Key required"); return; }
    if (draft.display_label.trim().length < 2) { toast.error("Label required"); return; }
    if (draft.modifier_clause.trim().length < 5) { toast.error("Clause too short"); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API}/v2/admin/cover-org-types/${encodeURIComponent(key)}`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ display_label: draft.display_label.trim(), modifier_clause: draft.modifier_clause.trim(), is_default: !!draft.is_default }),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e?.detail || `HTTP ${r.status}`); }
      toast.success("Org-type saved");
      setEditKey(null);
      fetchRows();
    } catch (e) { toast.error(`Save failed: ${e.message}`); }
    finally { setSaving(false); }
  };
  const remove = async (key) => {
    if (!window.confirm(`Delete org-type "${key}"?`)) return;
    try {
      const r = await fetch(`${API}/v2/admin/cover-org-types/${encodeURIComponent(key)}`, { method: "DELETE", credentials: "include" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success(`Deleted "${key}"`);
      fetchRows();
    } catch (e) { toast.error(`Delete failed: ${e.message}`); }
  };

  return (
    <Card data-testid="cover-admin-orgtypes-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2"><Building2 className="w-4 h-4 text-indigo-700" /> Organisation-type modifiers</CardTitle>
        <Button size="sm" variant="outline" onClick={startNew} data-testid="cover-admin-orgtype-add-btn"><Plus className="w-4 h-4 mr-1" /> New</Button>
      </CardHeader>
      <CardContent className="space-y-2">
        <p className="text-xs text-slate-500">Exactly one default. Setting a new default auto-clears the previous.</p>
        {editKey === "__new__" && <OrgTypeEditForm draft={draft} setDraft={setDraft} onSave={save} onCancel={cancel} saving={saving} isNew />}
        {loading ? (
          <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : (
          <ul className="divide-y divide-slate-200 border border-slate-200 rounded">
            {rows.map((row) => (
              <li key={row.org_type_key} className="px-3 py-2 text-sm" data-testid={`cover-admin-orgtype-${row.org_type_key}`}>
                {editKey === row.org_type_key ? (
                  <OrgTypeEditForm draft={draft} setDraft={setDraft} onSave={save} onCancel={cancel} saving={saving} />
                ) : (
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-900 flex items-center gap-2">
                        {row.display_label}
                        {row.is_default && <span className="text-[10px] bg-blue-100 text-blue-800 border border-blue-300 rounded px-1.5 py-0.5" data-testid={`cover-admin-orgtype-default-${row.org_type_key}`}>Default</span>}
                      </div>
                      <div className="text-[11px] text-slate-500 font-mono">{row.org_type_key}</div>
                      <div className="text-[11px] text-slate-600 mt-1 italic">{row.modifier_clause}</div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => startEdit(row)} className="text-slate-600 hover:text-slate-900 p-1" data-testid={`cover-admin-orgtype-edit-${row.org_type_key}`}><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => remove(row.org_type_key)} className="text-red-600 hover:text-red-700 p-1" data-testid={`cover-admin-orgtype-delete-${row.org_type_key}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function OrgTypeEditForm({ draft, setDraft, onSave, onCancel, saving, isNew }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded p-3 space-y-2" data-testid="cover-admin-orgtype-form">
      {isNew && (
        <div><Label className="text-xs">Key (lowercase, underscores)</Label>
          <Input value={draft.org_type_key} onChange={(e) => setDraft({ ...draft, org_type_key: e.target.value })} data-testid="cover-admin-orgtype-key-input" /></div>
      )}
      <div><Label className="text-xs">Display label</Label>
        <Input value={draft.display_label} onChange={(e) => setDraft({ ...draft, display_label: e.target.value })} data-testid="cover-admin-orgtype-label-input" /></div>
      <div><Label className="text-xs">Modifier clause (tonal frame, no sector/colour language)</Label>
        <Textarea value={draft.modifier_clause} onChange={(e) => setDraft({ ...draft, modifier_clause: e.target.value })} rows={2} data-testid="cover-admin-orgtype-clause-input" /></div>
      <div className="flex items-center gap-2">
        <Switch checked={draft.is_default} onCheckedChange={(v) => setDraft({ ...draft, is_default: v })} data-testid="cover-admin-orgtype-default-toggle" />
        <span className="text-xs text-slate-700">Make this the default selection</span>
      </div>
      <div className="flex gap-2">
        <Button size="sm" onClick={onSave} disabled={saving} className="bg-slate-800 hover:bg-slate-900 text-white" data-testid="cover-admin-orgtype-save-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}><X className="w-4 h-4 mr-1" /> Cancel</Button>
      </div>
    </div>
  );
}

// ─── Colour presets ─────────────────────────────────────────────────────
function ColourPresetsCard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editKey, setEditKey] = useState(null);
  const [draft, setDraft] = useState({ colour_key: "", display_label: "", clause_inserted_into_prompt: "", is_default: false });
  const [saving, setSaving] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/v2/admin/cover-colour-presets`, { credentials: "include" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setRows(j.colour_presets || []);
    } catch (e) { toast.error(`Load failed: ${e.message}`); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchRows(); }, [fetchRows]);

  const startEdit = (row) => { setEditKey(row.colour_key); setDraft({ ...row }); };
  const startNew = () => { setEditKey("__new__"); setDraft({ colour_key: "", display_label: "", clause_inserted_into_prompt: "", is_default: false }); };
  const cancel = () => setEditKey(null);

  const save = async () => {
    const key = (editKey === "__new__" ? draft.colour_key : editKey).trim().toLowerCase();
    if (!key) { toast.error("Key required"); return; }
    if (draft.display_label.trim().length < 2) { toast.error("Label required"); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API}/v2/admin/cover-colour-presets/${encodeURIComponent(key)}`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ display_label: draft.display_label.trim(), clause_inserted_into_prompt: (draft.clause_inserted_into_prompt || "").trim(), is_default: !!draft.is_default }),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e?.detail || `HTTP ${r.status}`); }
      toast.success("Colour preset saved");
      setEditKey(null);
      fetchRows();
    } catch (e) { toast.error(`Save failed: ${e.message}`); }
    finally { setSaving(false); }
  };
  const remove = async (key) => {
    if (!window.confirm(`Delete colour preset "${key}"?`)) return;
    try {
      const r = await fetch(`${API}/v2/admin/cover-colour-presets/${encodeURIComponent(key)}`, { method: "DELETE", credentials: "include" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success(`Deleted "${key}"`);
      fetchRows();
    } catch (e) { toast.error(`Delete failed: ${e.message}`); }
  };

  return (
    <Card data-testid="cover-admin-colours-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2"><Palette className="w-4 h-4 text-indigo-700" /> Colour presets</CardTitle>
        <Button size="sm" variant="outline" onClick={startNew} data-testid="cover-admin-colour-add-btn"><Plus className="w-4 h-4 mr-1" /> New</Button>
      </CardHeader>
      <CardContent className="space-y-2">
        <p className="text-xs text-slate-500">Exactly one default. The <span className="font-mono">custom</span> row reveals a free-text field in the wizard (leave its clause blank).</p>
        {editKey === "__new__" && <ColourEditForm draft={draft} setDraft={setDraft} onSave={save} onCancel={cancel} saving={saving} isNew />}
        {loading ? (
          <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : (
          <ul className="divide-y divide-slate-200 border border-slate-200 rounded">
            {rows.map((row) => (
              <li key={row.colour_key} className="px-3 py-2 text-sm" data-testid={`cover-admin-colour-${row.colour_key}`}>
                {editKey === row.colour_key ? (
                  <ColourEditForm draft={draft} setDraft={setDraft} onSave={save} onCancel={cancel} saving={saving} />
                ) : (
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-900 flex items-center gap-2">
                        {row.display_label}
                        {row.is_default && <span className="text-[10px] bg-blue-100 text-blue-800 border border-blue-300 rounded px-1.5 py-0.5" data-testid={`cover-admin-colour-default-${row.colour_key}`}>Default</span>}
                      </div>
                      <div className="text-[11px] text-slate-500 font-mono">{row.colour_key}</div>
                      {row.clause_inserted_into_prompt && <div className="text-[11px] text-slate-600 mt-1">{row.clause_inserted_into_prompt}</div>}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => startEdit(row)} className="text-slate-600 hover:text-slate-900 p-1" data-testid={`cover-admin-colour-edit-${row.colour_key}`}><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => remove(row.colour_key)} className="text-red-600 hover:text-red-700 p-1" data-testid={`cover-admin-colour-delete-${row.colour_key}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function ColourEditForm({ draft, setDraft, onSave, onCancel, saving, isNew }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded p-3 space-y-2" data-testid="cover-admin-colour-form">
      {isNew && (
        <div><Label className="text-xs">Key (lowercase, underscores)</Label>
          <Input value={draft.colour_key} onChange={(e) => setDraft({ ...draft, colour_key: e.target.value })} data-testid="cover-admin-colour-key-input" /></div>
      )}
      <div><Label className="text-xs">Display label</Label>
        <Input value={draft.display_label} onChange={(e) => setDraft({ ...draft, display_label: e.target.value })} data-testid="cover-admin-colour-label-input" /></div>
      <div><Label className="text-xs">Clause inserted into prompt (blank for custom)</Label>
        <Textarea value={draft.clause_inserted_into_prompt} onChange={(e) => setDraft({ ...draft, clause_inserted_into_prompt: e.target.value })} rows={2} data-testid="cover-admin-colour-clause-input" /></div>
      <div className="flex items-center gap-2">
        <Switch checked={draft.is_default} onCheckedChange={(v) => setDraft({ ...draft, is_default: v })} data-testid="cover-admin-colour-default-toggle" />
        <span className="text-xs text-slate-700">Make this the default selection</span>
      </div>
      <div className="flex gap-2">
        <Button size="sm" onClick={onSave} disabled={saving} className="bg-slate-800 hover:bg-slate-900 text-white" data-testid="cover-admin-colour-save-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}><X className="w-4 h-4 mr-1" /> Cancel</Button>
      </div>
    </div>
  );
}

// ─── Brand assets (logos + brand icon used by the compositor) ───────────
function BrandAssetsCard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/v2/admin/cover-brand-assets`, { credentials: "include" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setRows(j.assets || []);
    } catch (e) { toast.error(`Load failed: ${e.message}`); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchRows(); }, [fetchRows]);

  return (
    <Card data-testid="cover-admin-brand-assets-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <ImageIcon className="w-4 h-4 text-indigo-700" /> Brand assets
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <p className="text-xs text-slate-500">
          Logos and the brand icon the cover compositor stamps onto Templates A/B/C.
          Upload to override a bundled default; revert restores the shipped asset.
          PNG with transparency preferred — for the brand icon, a solid white
          background is removed automatically.
        </p>
        {loading ? (
          <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : (
          <ul className="divide-y divide-slate-200 border border-slate-200 rounded">
            {rows.map((row) => (
              <BrandAssetRow key={row.role} row={row} onChanged={fetchRows} />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function BrandAssetRow({ row, onChanged }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    let objUrl;
    let cancelled = false;
    (async () => {
      if (!row.has_asset) { setPreview(null); return; }
      try {
        const r = await fetch(`${API}/v2/admin/cover-brand-assets/${row.role}/preview?t=${row.updated_at || row.default_filename}`, { credentials: "include" });
        if (!r.ok) return;
        const blob = await r.blob();
        if (cancelled) return;
        objUrl = URL.createObjectURL(blob);
        setPreview(objUrl);
      } catch { /* preview is best-effort */ }
    })();
    return () => { cancelled = true; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [row.role, row.has_asset, row.updated_at, row.default_filename]);

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await fetch(`${API}/v2/admin/cover-brand-assets/${row.role}`, { method: "POST", credentials: "include", body: fd });
      if (!r.ok) { const er = await r.json().catch(() => ({})); throw new Error(er?.detail || `HTTP ${r.status}`); }
      toast.success(`${row.label} updated`);
      onChanged();
    } catch (e) { toast.error(`Upload failed: ${e.message}`); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const onRevert = async () => {
    if (!window.confirm(`Revert "${row.label}" to the bundled default?`)) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/v2/admin/cover-brand-assets/${row.role}`, { method: "DELETE", credentials: "include" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success(`${row.label} reverted to default`);
      onChanged();
    } catch (e) { toast.error(`Revert failed: ${e.message}`); }
    finally { setBusy(false); }
  };

  return (
    <li className="px-3 py-3 text-sm" data-testid={`cover-admin-brand-asset-${row.role}`}>
      <div className="flex items-start gap-3">
        <div
          className="w-20 h-20 shrink-0 rounded border border-slate-300 flex items-center justify-center overflow-hidden"
          style={{
            backgroundColor: "#e2e8f0",
            backgroundImage:
              "linear-gradient(45deg, #64748b 25%, transparent 25%), linear-gradient(-45deg, #64748b 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #64748b 75%), linear-gradient(-45deg, transparent 75%, #64748b 75%)",
            backgroundSize: "14px 14px",
            backgroundPosition: "0 0, 0 7px, 7px -7px, -7px 0",
          }}
          data-testid={`cover-admin-brand-preview-${row.role}`}
        >
          {preview ? (
            <img src={preview} alt={row.label} className="max-w-full max-h-full object-contain" />
          ) : (
            <ImageIcon className="w-6 h-6 text-slate-500" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-slate-900 flex items-center gap-2">
            {row.label}
            {row.is_managed ? (
              <span className="text-[10px] bg-amber-100 text-amber-800 border border-amber-300 rounded px-1.5 py-0.5" data-testid={`cover-admin-brand-custom-${row.role}`}>Custom</span>
            ) : (
              <span className="text-[10px] bg-slate-100 text-slate-600 border border-slate-300 rounded px-1.5 py-0.5" data-testid={`cover-admin-brand-default-${row.role}`}>Default</span>
            )}
          </div>
          <div className="text-[11px] text-slate-500 font-mono">{row.role}</div>
          <div className="text-[11px] text-slate-600 mt-1">{row.description}</div>
          {row.is_managed && row.width && (
            <div className="text-[11px] text-slate-400 mt-0.5">{row.width}×{row.height}px · {Math.round((row.bytes || 0) / 1024)} KB</div>
          )}
          <div className="flex items-center gap-2 mt-2">
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onUpload} data-testid={`cover-admin-brand-file-${row.role}`} />
            <Button size="sm" variant="outline" disabled={busy} onClick={() => fileRef.current?.click()} data-testid={`cover-admin-brand-upload-btn-${row.role}`}>
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />} Upload
            </Button>
            {row.is_managed && (
              <Button size="sm" variant="ghost" disabled={busy} onClick={onRevert} className="text-slate-600" data-testid={`cover-admin-brand-revert-btn-${row.role}`}>
                <RotateCcw className="w-4 h-4 mr-1" /> Revert
              </Button>
            )}
          </div>
        </div>
      </div>
    </li>
  );
}
